import { Box, ToggleButton } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, Outlet, useLocation, useParams } from "react-router-dom";
import PlaygroundUserToOrgConversionPopup from "../../components/Popups/PlaygroundUserToOrgConversionPopup/PlaygroundUserToOrgConversionPopup.tsx";
import BriaToggleButtonGroup from "../../components/common/BriaToggleButtonGroup/BriaToggleButtonGroup.tsx";
import { AspectRatio } from "../../components/common/DropDowns/StaticDropdown.tsx";
import { APPS } from "../../constants/AppsConstants.ts";
import RouterConstants from "../../constants/RouterConstants";
import { SESSION_STORAGE_KEYS, ssRemove } from "../../helpers/sessionStorage.ts";
import { useIframe } from "../../hooks/useIframe.tsx";
import useParentPath from "../../hooks/useParentPath.tsx";
import { useAppStore } from "../../hooks/useStores";
import { ImageEditingViewMode } from "../../models/image-to-image.ts";
import { IframePages } from "../../models/new-iframe.ts";
import { TextToImageConfigType } from "../../models/text-to-image.ts";
import { isFoxApps, isTextToVector } from "../../utils/index.ts";
import iframeStore from "../IframeNew/iframe-store.tsx";
import PlaygroundImages from "./Images/PlaygroundImages";
import styles from "./Playground.module.scss";

interface IPlayground {
	selectedConfig?: APPS;
	location?: {
		state: {
			prompt?: string;
			aspectRatio?: AspectRatio;
		};
	};
}

const Playground = ({ location: propLocation, selectedConfig }: IPlayground) => {
	const [playgroundClass, setPlaygroundClass] = useState("");
	const { navigateIframe } = useIframe();
	const { t } = useTranslation("translation", { keyPrefix: "playground" });
	const { t: tDemoBackBtn } = useTranslation("translation", { keyPrefix: "backBtn" });
	const { t: appsTrans } = useTranslation("translation", { keyPrefix: "appCards" });
	const { playgroundStore, uiStore, textToImageStore } = useAppStore();
	const { sessionsStore, projectsStore } = playgroundStore;
	const { session_id } = useParams();
	const routerLocation = useLocation();
	const location = propLocation || routerLocation;
	const pathname = routerLocation.pathname;
	const images = playgroundStore.playgroundResults.flatMap((results) =>
		results.images?.filter((image) => !image.error),
	);
	const parentPath = useParentPath(location);
	const isSingleMode =
		iframeStore.isIframe() && playgroundStore.selectedConfig === APPS.AUTOMOTIVE
			? playgroundStore.automotiveViewMode === ImageEditingViewMode.SINGLE
			: playgroundStore.imageEditingViewMode === ImageEditingViewMode.SINGLE;
	const navigateOptions = location.state;
	const generateFromNavigation = !!(
		navigateOptions &&
		navigateOptions.prompt &&
		((textToImageStore.config.model && textToImageStore.config.model_version) ||
			textToImageStore.config.tailored_model_id)
	);

	useEffect(() => {
		uiStore.hideSideBar();
		if (selectedConfig) {
			playgroundStore.selectedConfig = selectedConfig;
		}

		if (isFoxApps() || playgroundStore.selectedConfig !== APPS.IMAGE_TO_IMAGE) {
			playgroundStore.enablePlaygroundBrushMode = false;
			if (!iframeStore.isIframe()) playgroundStore.imageEditingViewMode = ImageEditingViewMode.GALLERY;
		}
		if (playgroundStore.selectedConfig === APPS.AUTOMOTIVE && !iframeStore.isIframe()) {
			playgroundStore.imageEditingViewMode = ImageEditingViewMode.SINGLE;
		}
		if (pathname.startsWith(RouterConstants.APPS.fullPath)) {
			setPlaygroundClass(styles.root);
		} else if (pathname === RouterConstants.CONSOLE.fullPath || (pathname.startsWith("/") && pathname.length > 1)) {
			setPlaygroundClass(styles.apiConsole);
		} else if (playgroundStore?.isText2ImagePopupOpened) {
			setPlaygroundClass(styles.rootModal);
		} else {
			setPlaygroundClass(styles.root);
		}

		if (
			playgroundStore.selectedConfig === APPS.IMAGE_TO_IMAGE ||
			(playgroundStore.selectedConfig === APPS.TEXT_TO_IMAGE && !isTextToVector())
		) {
			// clear ImageToImage or TextToImage results if coming from TextToVector
			if (images.some((image) => (image.config as TextToImageConfigType)?.format === "svg")) {
				playgroundStore.clearResults();
			}
		} else if (playgroundStore.selectedConfig === APPS.TEXT_TO_VECTOR || isTextToVector()) {
			// clear TextToVector results if coming from ImageToImage or TextToImage
			if (
				images.some(
					(image) =>
						image.type === APPS.IMAGE_TO_IMAGE || (image.config as TextToImageConfigType)?.format !== "svg",
				)
			) {
				playgroundStore.clearResults();
			}
		}

		const isImageToImage = playgroundStore.selectedConfig === APPS.IMAGE_TO_IMAGE && !isFoxApps();
		const isTextToImage = playgroundStore.selectedConfig === APPS.TEXT_TO_IMAGE;

		if (session_id) {
			generateAutomotiveBackButton(session_id);
		}
		(playgroundStore.selectedConfig === APPS.TEXT_TO_IMAGE || isImageToImage) &&
			uiStore.showBackButton(
				parentPath,
				undefined,
				parentPath.includes(RouterConstants.APPS.fullPath)
					? t(`${playgroundStore.selectedConfig}.backBtn`)
					: isImageToImage || isTextToImage
						? tDemoBackBtn("backToConsole")
						: undefined,
				undefined,
				undefined,
				undefined,
				parentPath.includes(RouterConstants.APPS.fullPath)
					? isImageToImage
						? appsTrans(`imageEditing.title`)
						: isTextToVector()
							? appsTrans(`textToVector.title`)
							: appsTrans(`imageGeneration.title`)
					: undefined,
			);

		playgroundStore.selectedConfig === APPS.PRODUCT_PLACEMENT &&
			uiStore.showBackButton(
				`/${RouterConstants.PRODUCT_PLACEMENT_GALLERY.fullPath}`,
				undefined,
				t(`${playgroundStore.selectedConfig}.backBtn`),
				undefined,
				undefined,
				undefined,
				t(`${playgroundStore.selectedConfig}.title`),
			);
		playgroundStore.selectedConfig === APPS.AUTOMOTIVE &&
			!session_id &&
			uiStore.showBackButton(
				RouterConstants.AUTOMOTIVE_PROJECTS.fullPath,
				undefined,
				undefined,
				undefined,
				undefined,
				undefined,
				undefined,
			);

		return () => {
			uiStore.showSideBar();
			uiStore.hideBackButton();
			// Clear feedback reminder flag when exiting playground to allow it to show again on next visit
			ssRemove(SESSION_STORAGE_KEYS.feedbackReminderShown);
		};
	}, [playgroundStore.selectedConfig, iframeStore.iframe?.config?.landing_page, session_id]);

	useEffect(() => {
		if (generateFromNavigation) {
			textToImageStore.handleConfigChange("prompt", navigateOptions.prompt);
			navigateOptions.aspectRatio &&
				textToImageStore.handleConfigChange("aspect_ratio", navigateOptions.aspectRatio);
			textToImageStore.generateTextToImage();
			window.history.replaceState({}, "");
		} else if (navigateOptions?.prompt === "" || navigateOptions?.aspectRatio) {
			textToImageStore.handleConfigChange("prompt", "");
			textToImageStore.handleConfigChange("aspect_ratio", "4:3");
		}
	}, [generateFromNavigation]);

	const generateAutomotiveBackButton = async (session_id: string) => {
		await getSessionAndProjectInfo(session_id);

		uiStore.showBackButton(
			`${RouterConstants.AUTOMOTIVE_PROJECT.path}/${projectsStore.project.id}/uploaded`,
			undefined,
			projectsStore.project.name,
			undefined,
			undefined,
			undefined,
			sessionsStore.sessionForm.name,
		);
	};

	const getSessionAndProjectInfo = async (session_id: string) => {
		await sessionsStore.getSession(session_id);
		await projectsStore.getProject(sessionsStore.sessionForm.project_id);
	};

	const handleConfigToggle = (_e: any, selectedConfig: APPS) => {
		playgroundStore.selectedConfig = selectedConfig;
		isSingleMode && selectedConfig === APPS.TEXT_TO_IMAGE && playgroundStore.togglePlaygroundViewMode();
		if (iframeStore.isIframe()) {
			navigateIframe(selectedConfig as unknown as IframePages);
		}
	};

	return (
		<Box className={playgroundClass}>
			<Box className={styles.configsWrapper}>
				{(playgroundStore.selectedConfig === APPS.TEXT_TO_IMAGE ||
					playgroundStore.selectedConfig === APPS.IMAGE_TO_IMAGE) &&
					!isTextToVector() &&
					(!iframeStore.isIframe() ||
						(iframeStore.iframe.config.enabled_pages.textToImage &&
							iframeStore.iframe.config.enabled_pages.imageToImage)) &&
					!images.some((image) => (image.config as TextToImageConfigType)?.format === "svg") &&
					!playgroundStore?.isText2ImagePopupOpened &&
					!isFoxApps() && (
						<BriaToggleButtonGroup
							className={styles.BriaToggleButtonGroup}
							value={playgroundStore.selectedConfig}
							onChange={handleConfigToggle}
						>
							<NavLink
								to={
									parentPath.includes(RouterConstants.APPS.fullPath)
										? RouterConstants.TEXT_TO_IMAGE.fullPath
										: RouterConstants.IMAGE_GENERATION.DEMO.IMAGE_GENERATION.fullPath
								}
								className={styles.fullSize}
							>
								<ToggleButton value="textToImage" className={styles.fullSize}>
									{t(`textToImage.toggleBtn`)}
								</ToggleButton>
							</NavLink>
							<NavLink
								to={
									parentPath.includes(RouterConstants.APPS.fullPath)
										? RouterConstants.IMAGE_TO_IMAGE.fullPath
										: RouterConstants.IMAGE_EDITING.IMAGE.DEMO.fullPath
								}
								className={styles.fullSize}
							>
								<ToggleButton value="imageToImage" className={styles.fullSize}>
									{t(`imageToImage.toggleBtn`)}
								</ToggleButton>
							</NavLink>
						</BriaToggleButtonGroup>
					)}
				<Outlet />
			</Box>
			<PlaygroundImages />
			<PlaygroundUserToOrgConversionPopup />
		</Box>
	);
};

export default observer(Playground);
